import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { CheckPicker, SelectPicker } from 'rsuite';
import { showToast } from '../../utils/toast';
import { useSaveTimesheetFavorites, useTimesheetFavorites } from '../../hooks/useTimesheet';
import { buildTaskOptions } from '../../utils/timesheet';

const MENU_WIDTH = 420;

// How far each level of the tree steps in, and the room a caret takes, so a
// leaf's name lines up with the names of the branches beside it.
const LEVEL_INDENT = 18;
const CARET_WIDTH = 19;
const BASE_INDENT = 12;
const indentFor = (level) => BASE_INDENT + Math.max(0, level - 1) * LEVEL_INDENT;

// The loggable tasks as a tree. A node whose parent is not among the options
// (archived, say, while it is kept for the current value) hangs off the
// nearest ancestor that is - found through the full task list - or sits at the
// top.
const buildForest = (options, tasks) => {
  const byId = new Map(options.map((o) => [o.value, { id: o.value, option: o, children: [] }]));
  const parentOf = new Map((tasks || []).map((t) => [t.id, t.parentId || null]));
  const roots = [];
  for (const node of byId.values()) {
    let parentId = node.option.parentId;
    const seen = new Set();
    while (parentId && !byId.has(parentId) && !seen.has(parentId)) {
      seen.add(parentId);
      parentId = parentOf.get(parentId) || null;
    }
    const parent = parentId ? byId.get(parentId) : null;
    if (parent && parent !== node) parent.children.push(node);
    else roots.push(node);
  }
  return { roots, byId };
};

/**
 * The task picker: every loggable task, in its place in the project tree,
 * searchable by any part of its path or its code.
 *
 * rsuite draws the toggle and places the popup; the menu inside is our own, so
 * every level of the tree can start folded (all but the path to the current
 * value), open and close with an animation, and sit under a short Favorites
 * list - the person's own pinned tasks, one click away whatever is folded. The
 * star on any row pins or unpins it.
 *
 * A node with tasks under it is a heading: clicking it opens and closes it,
 * and a Select button there (on hover) picks the node itself. A node with
 * nothing under it is picked by clicking it.
 *
 * Searching shows every match in its place, with the branches above it opened
 * for context.
 *
 * Keyboard: type to search, arrows to move, Enter to pick (or open/close a
 * heading; on a heading while searching, or one with nothing under it, it
 * picks it), right to open a heading or step into it, left to close it or step
 * out to its parent, Escape to close.
 *
 * `multiple` makes it a pick-several: `value` and `onChange` are arrays, each
 * row has a checkbox, and picking one toggles it without closing the menu.
 * `favorites={false}` leaves the Favorites list and the stars out - for
 * pickers that are not about logging time, like a rule's tasks.
 */
export default function TaskPicker({
  tasks,
  value,
  onChange,
  includeIds = [],
  disabledItemValues = [],
  placeholder = 'Pick a project / task',
  multiple = false,
  favorites: showFavorites = true,
  defaultOpen = false,
  onOpen,
  onClose,
  ...rest
}) {
  // Whatever is picked, as a list either way.
  const values = multiple ? (Array.isArray(value) ? value : []) : (value ? [value] : []);
  const isChosen = (id) => values.includes(id);
  const options = useMemo(
    () => buildTaskOptions(tasks, { includeIds: [...values, ...includeIds] }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tasks, values.join(','), includeIds.join(',')]
  );
  const optionById = useMemo(() => new Map(options.map((o) => [o.value, o])), [options]);
  const forest = useMemo(() => buildForest(options, tasks), [options, tasks]);
  const disabledIds = useMemo(() => new Set(disabledItemValues), [disabledItemValues]);

  const favoritesQuery = useTimesheetFavorites({ enabled: showFavorites });
  const saveFavorites = useSaveTimesheetFavorites();
  const maxFavorites = favoritesQuery.data?.max || 5;
  const favoriteIds = useMemo(
    () => (favoritesQuery.data?.taskIds || []).filter((id) => optionById.has(id)),
    [favoritesQuery.data, optionById]
  );
  const favoriteSet = new Set(favoriteIds);

  // Every branch above a node, so the path to the current value can be opened.
  const ancestorsOf = (id) => {
    const out = [];
    const seen = new Set();
    let parentId = optionById.get(id)?.parentId;
    while (parentId && optionById.has(parentId) && !seen.has(parentId)) {
      seen.add(parentId);
      out.push(parentId);
      parentId = optionById.get(parentId)?.parentId;
    }
    return out;
  };

  const [open, setOpen] = useState(defaultOpen);
  const [query, setQuery] = useState('');
  const openPaths = () => new Set(values.flatMap((id) => ancestorsOf(id)));
  const [expanded, setExpanded] = useState(openPaths);
  const [active, setActive] = useState(null);
  const [favoriteAnnouncement, setFavoriteAnnouncement] = useState('');
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const pickerRef = useRef(null);
  const pickerId = useId().replace(/:/g, '');
  const treeId = `${pickerId}-tree`;
  const helpId = `${pickerId}-keyboard-help`;
  const optionDomId = (key) => `${pickerId}-option-${key.replace(/[^a-zA-Z0-9_-]/g, '-')}`;

  // Back to the toggle once the menu goes, as rsuite's own menu does, so the
  // keyboard carries on from the field rather than from the page's end.
  const focusToggle = () => pickerRef.current?.target?.focus?.();

  // Folded state is set in the same update that opens the menu, so the path to
  // the value is already open on the first frame rather than animating open.
  const openMenu = () => {
    setQuery('');
    setActive(null);
    setExpanded(openPaths());
    setOpen(true);
    onOpen?.();
  };
  const closeMenu = () => {
    setOpen(false);
    onClose?.();
  };

  useEffect(() => {
    if (!open) return undefined;
    const frame = requestAnimationFrame(() => {
      inputRef.current?.focus();
      // The value in the tree, not its copy among the favorites.
      listRef.current?.querySelector(':scope > :not(.ts-tp__section) .is-selected')?.scrollIntoView({ block: 'nearest' });
    });
    return () => cancelAnimationFrame(frame);
  }, [open]);

  const keyword = query.trim().toLowerCase();
  const searching = keyword !== '';

  // The tree as shown: everything, or while searching the nodes that match
  // (a path match, so a project's name brings its whole tree) and the branches
  // above them. Each entry is { node, children, count }, count being how many
  // nodes are under it.
  const visible = useMemo(() => {
    const matches = (o) => String(o.path || '').toLowerCase().includes(keyword)
      || String(o.code || '').toLowerCase().includes(keyword);
    const walk = (nodes) => nodes.flatMap((node) => {
      const children = walk(node.children);
      if (searching && !children.length && !matches(node.option)) return [];
      const count = children.reduce((sum, c) => sum + 1 + c.count, 0);
      return [{ node, children, count }];
    });
    return walk(forest.roots);
  }, [forest, keyword, searching]);

  const favorites = searching || !showFavorites ? [] : favoriteIds.map((id) => optionById.get(id));
  const isOpenNode = (entry) => entry.children.length > 0 && (searching || expanded.has(entry.node.id));

  // What the arrow keys walk through, in screen order: favorites, then the
  // tree, depth first, into the branches that are open. A heading is a project
  // or anything with nodes under it.
  const nav = [];
  favorites.forEach((o) => nav.push({ key: `fav:${o.value}`, kind: 'item', id: o.value, parentKey: null }));
  const walkNav = (entries, level, parentKey) => entries.forEach((entry) => {
    const { id } = entry.node;
    const heading = level === 0 || entry.children.length > 0;
    const key = `node:${id}`;
    nav.push({
      key,
      kind: heading ? 'group' : 'item',
      id,
      hasItems: entry.children.length > 0,
      selectable: !disabledIds.has(id),
      parentKey,
    });
    if (isOpenNode(entry)) walkNav(entry.children, level + 1, key);
  });
  walkNav(visible, 0, null);

  // While searching, Enter should take the first match without arrowing to it:
  // the first thing with nothing under it, or failing that the first heading.
  useEffect(() => {
    if (!searching) return;
    const first = nav.find((n) => n.kind === 'item' && n.selectable && !n.key.startsWith('fav:'))
      || nav.find((n) => n.selectable);
    setActive(first ? first.key : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword]);

  const toggleNode = (id, force) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      const want = force ?? !next.has(id);
      if (want) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  // One pick closes the menu; in a pick-several it toggles and stays open.
  const select = (id) => {
    if (disabledIds.has(id)) return;
    if (multiple) {
      onChange(isChosen(id) ? values.filter((v) => v !== id) : [...values, id]);
      return;
    }
    onChange(id);
    closeMenu();
    focusToggle();
  };

  const toggleFavorite = (id, announce = false) => {
    const next = favoriteSet.has(id)
      ? favoriteIds.filter((f) => f !== id)
      : [...favoriteIds, id];
    if (next.length > maxFavorites) {
      const message = `You can keep up to ${maxFavorites} favorites. Unstar one first.`;
      showToast('warning', message);
      if (announce) setFavoriteAnnouncement(message);
      return;
    }
    const name = optionById.get(id)?.name || 'Task';
    saveFavorites.mutate(next, {
      onSuccess: () => {
        if (announce) setFavoriteAnnouncement(`${name} ${favoriteSet.has(id) ? 'removed from' : 'added to'} favorites.`);
      },
      onError: (err) => {
        showToast('error', String(err?.message || err));
        if (announce) setFavoriteAnnouncement(`Could not update ${name} favorites.`);
      },
    });
  };

  const goTo = (key) => {
    setActive(key);
    listRef.current?.querySelector(`[data-nav="${key}"]`)?.scrollIntoView({ block: 'nearest' });
  };

  const moveActive = (step) => {
    if (nav.length === 0) return;
    const at = nav.findIndex((n) => n.key === active);
    const nextIndex = at === -1 ? (step > 0 ? 0 : nav.length - 1) : Math.min(nav.length - 1, Math.max(0, at + step));
    goTo(nav[nextIndex].key);
  };

  const onKeyDown = (e) => {
    const at = nav.findIndex((n) => n.key === active);
    const current = at === -1 ? null : nav[at];
    const isOpen = current?.kind === 'group' && current.hasItems && (searching || expanded.has(current.id));
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      moveActive(e.key === 'ArrowDown' ? 1 : -1);
    } else if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'f') {
      e.preventDefault();
      if (showFavorites && current) toggleFavorite(current.id, true);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (current?.kind === 'item') select(current.id);
      else if (current?.kind === 'group') {
        if (current.hasItems && !searching) toggleNode(current.id);
        else if (current.selectable) select(current.id);
      }
    } else if (e.key === 'ArrowRight' && current?.kind === 'group' && current.hasItems) {
      e.preventDefault();
      if (!isOpen) toggleNode(current.id, true);
      else if (nav[at + 1]) goTo(nav[at + 1].key);
    } else if (e.key === 'ArrowLeft' && current && !current.key.startsWith('fav:')) {
      // Close an open heading; anywhere else, step out to the heading above.
      if (isOpen && !searching) {
        e.preventDefault();
        toggleNode(current.id, false);
      } else if (current.parentKey) {
        e.preventDefault();
        goTo(current.parentKey);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeMenu();
      focusToggle();
    } else if (e.key === 'Tab') {
      // Focus goes to the toggle first, so the Tab itself moves on from there.
      focusToggle();
      closeMenu();
    }
  };

  const starButton = (id) => {
    if (!showFavorites) return null;
    const isFavorite = favoriteSet.has(id);
    return (
      <button
        type="button"
        tabIndex={-1}
        className={`ts-tp__star${isFavorite ? ' is-favorite' : ''}`}
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.stopPropagation();
          toggleFavorite(id);
        }}
        aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
        title={isFavorite ? 'Remove from favorites' : `Add to favorites (up to ${maxFavorites})`}
      >
        <i className={`bi ${isFavorite ? 'bi-star-fill' : 'bi-star'}`} aria-hidden="true" />
      </button>
    );
  };

  const checkbox = (id) => (multiple ? (
    <i className={`bi ${isChosen(id) ? 'bi-check-square-fill' : 'bi-square'} ts-tp__check`} aria-hidden="true" />
  ) : null);

  const rdTag = (o) => (o.rdEligible ? <span className="ts-rdtag" title="R&D credit eligible">R&amp;D</span> : null);

  // Billable and the task count sit in fixed slots on every row, so the
  // dollar signs and the numbers each line up down the list.
  const meta = (o, count = null) => (
    <span className="ts-tp__meta">
      <span className="ts-billable" title={o.billable ? 'Billable' : undefined}>
        {o.billable ? <i className="bi bi-currency-dollar" aria-hidden="true" /> : null}
      </span>
      <span className="ts-tp__count">{count}</span>
    </span>
  );

  // A favorite reads as its path below the project, with the project under it
  // - or, for a whole project, the project's name.
  const renderFavorite = (o) => {
    const key = `fav:${o.value}`;
    const isDisabled = disabledIds.has(o.value);
    const isProject = o.value === o.rootId;
    return (
      <div
        key={key}
        id={optionDomId(key)}
        data-nav={key}
        role="treeitem"
        aria-level={1}
        aria-selected={isChosen(o.value)}
        aria-disabled={isDisabled || undefined}
        className={`ts-tp__item${active === key ? ' is-active' : ''}${isChosen(o.value) ? ' is-selected' : ''}${isDisabled ? ' is-disabled' : ''}`}
        onMouseDown={(e) => e.preventDefault()}
        onMouseEnter={() => setActive(key)}
        onClick={() => select(o.value)}
        title={o.path}
      >
        <span className="ts-tp__label">
          {isProject ? o.project : o.label}
          <span className="ts-tp__sub">{isProject ? 'Whole project' : o.project}</span>
        </span>
        {rdTag(o)}
        {meta(o)}
        {starButton(o.value)}
      </div>
    );
  };

  // One node of the tree. A heading (a project, or anything with nodes under
  // it) has a caret and opens and closes; a leaf is picked by clicking it.
  const renderNode = (entry, level) => {
    const { node, children, count } = entry;
    const o = node.option;
    const key = `node:${node.id}`;
    const hasItems = children.length > 0;
    const heading = level === 0 || hasItems;
    const isOpen = isOpenNode(entry);
    const isDisabled = disabledIds.has(node.id);
    const indent = indentFor(level);

    if (!heading) {
      return (
        <div
          key={node.id}
          id={optionDomId(key)}
          data-nav={key}
          role="treeitem"
          aria-level={level + 1}
          aria-selected={isChosen(node.id)}
          aria-disabled={isDisabled || undefined}
          className={`ts-tp__item${active === key ? ' is-active' : ''}${isChosen(node.id) ? ' is-selected' : ''}${isDisabled ? ' is-disabled' : ''}`}
          style={{ paddingLeft: indent + CARET_WIDTH }}
          onMouseDown={(e) => e.preventDefault()}
          onMouseEnter={() => setActive(key)}
          onClick={() => select(node.id)}
          title={o.path}
        >
          {checkbox(node.id)}
          <span className="ts-tp__label">{o.name}</span>
          {rdTag(o)}
          {meta(o)}
          {starButton(node.id)}
        </div>
      );
    }

    return (
      <div
        key={node.id}
        id={optionDomId(key)}
        data-nav={key}
        role="treeitem"
        aria-level={level + 1}
        aria-expanded={hasItems ? isOpen : undefined}
        aria-selected={isChosen(node.id)}
        aria-disabled={isDisabled || undefined}
        aria-label={o.name}
        className="ts-tp__group"
      >
        <div
          role="presentation"
          tabIndex={-1}
          className={`ts-tp__grouphead${level > 0 ? ' ts-tp__grouphead--nested' : ''}${active === key ? ' is-active' : ''}${isChosen(node.id) ? ' is-selected' : ''}${!hasItems && isDisabled ? ' is-disabled' : ''}`}
          style={level > 0 ? { paddingLeft: indent } : undefined}
          onMouseDown={(e) => e.preventDefault()}
          onMouseEnter={() => setActive(key)}
          onClick={() => {
            if (hasItems) toggleNode(node.id);
            else if (!isDisabled) select(node.id);
          }}
          title={o.path}
        >
          <i className={`bi bi-chevron-right ts-tp__caret${hasItems ? '' : ' is-hidden'}`} aria-hidden="true" />
          {hasItems ? null : checkbox(node.id)}
          <span className="ts-tp__grouptitle">{o.name}</span>
          {rdTag(o)}
          {hasItems && !isDisabled ? (
            <button
              type="button"
              tabIndex={-1}
              className={`ts-tp__pick${isChosen(node.id) ? ' is-on' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => {
                e.stopPropagation();
                select(node.id);
              }}
              title={multiple ? `${isChosen(node.id) ? 'Take out' : 'Pick'} ${o.name} and everything under it` : `Log time to ${o.name} itself`}
            >
              {multiple && isChosen(node.id) ? (
                <>
                  <i className="bi bi-check-lg" aria-hidden="true" />
                  Selected
                </>
              ) : 'Select'}
            </button>
          ) : null}
          {meta(o, hasItems ? count : null)}
          {starButton(node.id)}
        </div>
        {hasItems ? (
          <div className={`ts-tp__body${isOpen ? ' is-open' : ''}`} role="group" aria-label={`${o.name} tasks`} inert={!isOpen}>
            <div className="ts-tp__bodyinner" role="presentation">
              {children.map((child) => renderNode(child, level + 1))}
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  const menu = (
    // Our keys stay ours: rsuite's own list handling would act on items it
    // is not showing.
    <div className={`ts-tp${searching ? ' ts-tp--searching' : ''}`} onKeyDown={(e) => e.stopPropagation()} role="presentation">
      <div className="ts-tp__search">
        <i className="bi bi-search" aria-hidden="true" />
        <input
          ref={inputRef}
          role="combobox"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Search projects and tasks"
          aria-label="Search projects and tasks"
          aria-autocomplete="list"
          aria-haspopup="tree"
          aria-expanded={open}
          aria-controls={treeId}
          aria-activedescendant={active && nav.some((n) => n.key === active) ? optionDomId(active) : undefined}
          aria-keyshortcuts="Control+Shift+F Meta+Shift+F"
          aria-describedby={helpId}
          autoComplete="off"
          spellCheck={false}
        />
      </div>
      <div id={treeId} className="ts-tp__list" ref={listRef} role="tree">
        {favorites.length ? (
          <div className="ts-tp__section" role="group" aria-label="Favorites">
            <div className="ts-tp__heading" role="presentation">
              <i className="bi bi-star-fill" aria-hidden="true" />
              Favorites
            </div>
            {favorites.map(renderFavorite)}
          </div>
        ) : null}
        {visible.map((entry) => renderNode(entry, 0))}
        {visible.length === 0 ? <div className="ts-tp__empty" role="presentation">No matching tasks</div> : null}
      </div>
      <span id={helpId} className="visually-hidden">Use Control or Command plus Shift plus F to pin or unpin the active task.</span>
      <span className="visually-hidden" aria-live="polite" aria-atomic="true">{favoriteAnnouncement}</span>
    </div>
  );

  const Picker = multiple ? CheckPicker : SelectPicker;
  return (
    <Picker
      ref={pickerRef}
      data={options}
      value={multiple ? values : value || null}
      onChange={(next) => onChange(multiple ? next || [] : next || null)}
      renderValue={multiple
        ? (v, items) => (items.length <= 2 ? items.map((item) => item.path).join(', ') : `${items.length} selected`)
        : (v, item) => (item ? item.path : v)}
      renderMenu={() => menu}
      searchable={false}
      open={open}
      onOpen={openMenu}
      onClose={closeMenu}
      placeholder={placeholder}
      menuAutoWidth={false}
      menuStyle={{ width: MENU_WIDTH, maxWidth: 'calc(100vw - 32px)' }}
      menuClassName="ts-tp-popup"
      block
      preventOverflow
      {...rest}
    />
  );
}
